//DQAssesmentReport.tsx
import React, { useState, useEffect, useCallback } from "react";
import { getAssessment, type DQAssessment } from "../API";

// ─── theme (matches the rest of the app) ──────────────────────────────────────
const T = {
  bg: '#eef1f6', bgCard: '#ffffff', bgSidebar: '#f5f7fa',
  textPrimary: '#0f1923', textSecond: '#1e2d3d', textMuted: '#445566',
  brand: '#1e3a6e', brandLight: '#dde7f5', brandMid: '#90aed0', border: '#c8d4e0',
  green: '#166534', greenBg: '#f0fdf4', greenBorder: '#86efac',
  amber: '#92400e', amberBg: '#fffbeb', amberBorder: '#fcd34d',
  red: '#991b1b', redBg: '#fef2f2', redBorder: '#fca5a5',
  purple: '#5b21b6', purpleBg: '#f5f3ff', purpleBorder: '#c4b5fd',
};

// ─── disposition palette ──────────────────────────────────────────────────────
const DISP: Record<string, { bg: string; fg: string; label: string }> = {
  CARRY:     { bg: T.greenBg,  fg: T.green,  label: 'Carry' },
  TRANSFORM: { bg: T.brandLight, fg: T.brand, label: 'Transform' },
  REVIEW:    { bg: T.amberBg,  fg: T.amber,  label: 'Review' },
  DEFAULT:   { bg: T.purpleBg, fg: T.purple, label: 'Default' },
  DROP:      { bg: '#f1f5f9',  fg: '#64748b', label: 'Drop' },
  BLOCKER:   { bg: T.redBg,    fg: T.red,    label: 'Blocker' },
};
const SEV: Record<string, { bg: string; fg: string }> = {
  H: { bg: T.redBg, fg: T.red }, M: { bg: T.amberBg, fg: T.amber }, L: { bg: T.bgSidebar, fg: T.textMuted },
};
const LANE: Record<string, { label: string; desc: string; color: string; bg: string }> = {
  auto:     { label: 'Automated', desc: 'Deterministic cleanse — applied automatically, logged for audit', color: T.green, bg: T.greenBg },
  assisted: { label: 'Assisted (LLM + SME review)', desc: 'Proposed fix with a confidence score; reviewed by an analyst, most-affected first', color: T.amber, bg: T.amberBg },
  manual:   { label: 'Manual (SME)', desc: 'Must-fix issues needing a human or source-system decision', color: T.red, bg: T.redBg },
};

const readinessColor = (s: number) => (s >= 80 ? T.green : s >= 60 ? T.amber : T.red);
const readinessBg    = (s: number) => (s >= 80 ? T.greenBg : s >= 60 ? T.amberBg : T.redBg);
const fmt = (n: number) => (n ?? 0).toLocaleString();

// ─── small presentational helpers ─────────────────────────────────────────────
const Pill: React.FC<{ bg: string; fg: string; children: React.ReactNode }> = ({ bg, fg, children }) => (
  <span style={{ background: bg, color: fg, fontSize: 11, fontWeight: 700, padding: '2px 8px', borderRadius: 10, whiteSpace: 'nowrap' }}>{children}</span>
);

const Bars: React.FC<{ data: Record<string, number>; color?: string }> = ({ data, color = T.brand }) => {
  const entries = Object.entries(data || {}).filter(([, v]) => v > 0).sort((a, b) => b[1] - a[1]);
  const max = Math.max(1, ...entries.map(([, v]) => v));
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      {entries.map(([k, v]) => (
        <div key={k} style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12 }}>
          <div style={{ width: 110, color: T.textMuted, textAlign: 'right', flexShrink: 0 }}>{k}</div>
          <div style={{ flex: 1, background: T.bgSidebar, borderRadius: 4, height: 14, overflow: 'hidden' }}>
            <div style={{ width: `${(v / max) * 100}%`, height: '100%', background: color, borderRadius: 4 }} />
          </div>
          <div style={{ width: 36, fontWeight: 700, color: T.textSecond }}>{v}</div>
        </div>
      ))}
    </div>
  );
};

const ScoreRing: React.FC<{ score: number; size?: number }> = ({ score, size = 92 }) => {
  const r = (size - 12) / 2, c = 2 * Math.PI * r, off = c * (1 - score / 100);
  const col = readinessColor(score);
  return (
    <svg width={size} height={size} viewBox={`0 0 ${size} ${size}`}>
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={T.border} strokeWidth={8} />
      <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke={col} strokeWidth={8} strokeLinecap="round"
        strokeDasharray={c} strokeDashoffset={off} transform={`rotate(-90 ${size / 2} ${size / 2})`} />
      <text x="50%" y="48%" textAnchor="middle" fontSize={size * 0.26} fontWeight={800} fill={col}>{Math.round(score)}</text>
      <text x="50%" y="68%" textAnchor="middle" fontSize={size * 0.12} fill={T.textMuted}>/ 100</text>
    </svg>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Phase 1 — Readiness + Current Data Insight
// ─────────────────────────────────────────────────────────────────────────────
const Phase1: React.FC<{ a: DQAssessment }> = ({ a }) => {
  const [open, setOpen] = useState<Record<string, boolean>>({});
  const toggle = (t: string) => setOpen(o => ({ ...o, [t]: !o[t] }));

  const ex = a.rollup?.execution;
  return (
    <div>
      {/* measured current-state health */}
      {ex && (ex.rules_pass + ex.rules_fail) > 0 && (
        <div style={{ ...card, marginBottom: 20, borderLeft: `4px solid ${ex.rules_fail ? T.red : T.green}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 28, flexWrap: 'wrap' }}>
            <div>
              <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 600 }}>Current data quality (measured by running the rules)</div>
              <div style={{ fontSize: 28, fontWeight: 800, color: ex.pass_rate >= 80 ? T.green : ex.pass_rate >= 60 ? T.amber : T.red }}>
                {ex.pass_rate != null ? `${ex.pass_rate}%` : '—'} <span style={{ fontSize: 13, fontWeight: 600, color: T.textMuted }}>of checks pass</span>
              </div>
              <div style={{ fontSize: 12, color: T.textMuted, marginTop: 2 }}>{fmt(ex.rules_pass)} pass · {fmt(ex.rules_fail)} fail · {fmt(ex.rules_applied)} auto-cleansed</div>
            </div>
            <div style={{ display: 'flex', gap: 24 }}>
              <div><div style={{ fontSize: 22, fontWeight: 800, color: T.red }}>{fmt(ex.fields_failing)}</div><div style={{ fontSize: 11, color: T.textMuted }}>fields need attention</div></div>
              <div><div style={{ fontSize: 22, fontWeight: 800, color: T.green }}>{fmt(ex.fields_cleansed)}</div><div style={{ fontSize: 11, color: T.textMuted }}>fields auto-cleansed</div></div>
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <div style={sub}>Where the failures are (by quality dimension)</div>
              <Bars data={ex.fail_by_dimension} color={T.red} />
            </div>
          </div>
        </div>
      )}

      {/* overall scorecard */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        <div style={{ ...card, display: 'flex', alignItems: 'center', gap: 16, minWidth: 280 }}>
          <ScoreRing score={a.overall.readiness} />
          <div>
            <div style={{ fontSize: 12, color: T.textMuted, fontWeight: 600 }}>Overall migration readiness</div>
            <div style={{ fontSize: 13, color: T.textSecond, marginTop: 4 }}>
              {a.overall.objects} object(s) · {fmt(a.overall.total_records)} records · {a.overall.total_rules} rules
            </div>
            {a.overall.blocker_count > 0
              ? <div style={{ marginTop: 8 }}><Pill bg={T.redBg} fg={T.red}>{a.overall.blocker_count} blocker finding(s)</Pill></div>
              : <div style={{ marginTop: 8 }}><Pill bg={T.greenBg} fg={T.green}>No blocking findings</Pill></div>}
          </div>
        </div>
        <div style={{ ...card, flex: 1, minWidth: 240 }}>
          <div style={sub}>Rules by dimension</div>
          <Bars data={a.rollup.by_dimension} />
        </div>
        <div style={{ ...card, flex: 1, minWidth: 200 }}>
          <div style={sub}>Rules by severity</div>
          <Bars data={a.rollup.by_severity} color={T.red} />
        </div>
      </div>

      {/* per-object */}
      {a.tables.map((t: any) => {
        const isOpen = !!open[t.table];
        return (
          <div key={t.table} style={{ ...card, marginBottom: 14, padding: 0, overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 14, padding: '14px 18px', borderBottom: isOpen ? `1px solid ${T.border}` : 'none' }}>
              <div style={{ width: 46, height: 46, borderRadius: 10, background: readinessBg(t.readiness), color: readinessColor(t.readiness), display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 800, fontSize: 16, flexShrink: 0 }}>
                {Math.round(t.readiness)}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontWeight: 800, color: T.brand, fontSize: 15 }}>{t.table}</div>
                <div style={{ fontSize: 12, color: T.textMuted }}>{fmt(t.num_rows)} records · {t.num_columns} fields</div>
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {Object.entries(t.buckets).filter(([, v]: any) => v > 0).map(([k, v]: any) => (
                  <Pill key={k} bg={(DISP[k] || DISP.CARRY).bg} fg={(DISP[k] || DISP.CARRY).fg}>{(DISP[k] || { label: k }).label} {v}</Pill>
                ))}
              </div>
              <button onClick={() => toggle(t.table)} style={linkBtn}>{isOpen ? 'Hide fields ▲' : 'Field detail ▼'}</button>
            </div>

            {/* findings (business language) */}
            <div style={{ padding: '12px 18px' }}>
              {t.findings.length === 0 && <div style={{ fontSize: 13, color: T.textMuted }}>No notable findings.</div>}
              {t.findings.map((f: any, i: number) => {
                const c = f.severity === 'high' ? SEV.H : f.severity === 'warn' ? SEV.M : { bg: T.bgSidebar, fg: T.textMuted };
                return (
                  <div key={i} style={{ padding: '6px 0' }}>
                    <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', fontSize: 13, color: T.textSecond }}>
                      <span style={{ width: 8, height: 8, borderRadius: '50%', background: c.fg, marginTop: 6, flexShrink: 0 }} />
                      <span>{f.text}</span>
                    </div>
                    {f.evidence?.length > 0 && (
                      <ul style={{ margin: '4px 0 0 28px', padding: 0, fontSize: 12, color: T.textMuted }}>
                        {f.evidence.map((e: string, j: number) => <li key={j} style={{ listStyle: 'disc', marginLeft: 14 }}>{e}</li>)}
                      </ul>
                    )}
                    {f.basis && <div style={{ margin: '2px 0 0 28px', fontSize: 11, color: T.brandMid, fontStyle: 'italic' }}>Basis: {f.basis}</div>}
                  </div>
                );
              })}
              {t.deductions?.length > 0 && (
                <div style={{ marginTop: 8, fontSize: 12, color: T.textMuted }}>
                  <b>Score breakdown:</b> {t.deductions.map((d: any, i: number) => (
                    <span key={i}>{i > 0 ? ' · ' : ' '}<span style={{ color: T.red, fontWeight: 700 }}>{d.points}</span> {d.reason}</span>
                  ))}
                </div>
              )}
            </div>

            {/* field disposition table */}
            {isOpen && (
              <div style={{ padding: '0 18px 16px', overflowX: 'auto' }}>
                <table style={tbl}>
                  <thead><tr>{['Field', 'Disposition', 'Missing %', 'Required', 'Type', 'Reason'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {t.dispositions.map((d: any) => {
                      const dp = DISP[d.disposition] || DISP.CARRY;
                      return (
                        <tr key={d.column}>
                          <td style={td}><b>{d.column}</b></td>
                          <td style={td}><Pill bg={dp.bg} fg={dp.fg}>{dp.label}</Pill></td>
                          <td style={td}>{d.missing_pct != null ? `${d.missing_pct}%` : '—'}</td>
                          <td style={td}>{d.mandatory ? 'Yes' : ''}</td>
                          <td style={{ ...td, color: T.textMuted }}>{d.recommended_type || d.data_type || ''}</td>
                          <td style={{ ...td, color: T.textMuted }}>{d.reason}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────────
// Phase 2 — Remediation / Apply
// ─────────────────────────────────────────────────────────────────────────────
const Phase2: React.FC<{ a: DQAssessment }> = ({ a }) => {
  const { counts, lanes, sme_queue } = a.apply;
  const auto_summary = a.apply.auto_summary || ({} as any);
  const rationale = a.apply.rationale || ({} as any);
  const laneKeys: ('auto' | 'assisted' | 'manual')[] = ['auto', 'assisted', 'manual'];
  const [openChanges, setOpenChanges] = useState<Record<string, boolean>>({});

  const recordsCell = (count?: number, pct?: number) =>
    count != null ? `${fmt(count)}${pct != null ? ` (${pct}%)` : ''}` : '—';

  return (
    <div>
      {/* lane summary — what happens to the data */}
      <div style={{ display: 'flex', gap: 16, flexWrap: 'wrap', marginBottom: 20 }}>
        {laneKeys.map(k => (
          <div key={k} style={{ ...card, flex: 1, minWidth: 240, borderTop: `3px solid ${LANE[k].color}` }}>
            <div style={{ fontSize: 26, fontWeight: 800, color: LANE[k].color }}>{counts[k]}</div>
            <div style={{ fontWeight: 700, color: T.textSecond, fontSize: 13 }}>{LANE[k].label}</div>
            <div style={{ fontSize: 12, color: T.textMuted, marginTop: 4 }}>
              {k === 'auto'
                ? `${auto_summary.applied || 0} applied · ${auto_summary.no_change_needed || 0} already clean`
                : `${lanes[k].filter((r: any) => (r.fail_count || 0) > 0).length} with records currently failing`}
            </div>
          </div>
        ))}
      </div>

      {/* lanes detail */}
      {laneKeys.map(k => (
        <div key={k} style={{ ...card, marginBottom: 14 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
            <span style={{ width: 10, height: 10, borderRadius: '50%', background: LANE[k].color }} />
            <span style={{ fontWeight: 800, color: T.textSecond }}>{LANE[k].label}</span>
            <span style={{ fontSize: 12, color: T.textMuted }}>({lanes[k].length})</span>
          </div>
          <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 10 }}>{rationale[k]}</div>
          {lanes[k].length === 0 ? <div style={{ fontSize: 13, color: T.textMuted }}>None.</div> : (
            <div style={{ overflowX: 'auto' }}>
              {k === 'auto' ? (
                <table style={tbl}>
                  <thead><tr>{['Field', 'What the cleanse does', 'Status', 'Records changed', ''].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {lanes.auto.slice(0, 40).map((r: any, i: number) => {
                      const key = r.id || `a${i}`;
                      const has = r.sample_changes && r.sample_changes.length > 0;
                      return (
                        <React.Fragment key={key}>
                          <tr>
                            <td style={td}><b>{Array.isArray(r.field) ? r.field.join(', ') : r.field}</b></td>
                            <td style={{ ...td, maxWidth: 320, color: T.textSecond }}>{r.business_impact || r.fix}</td>
                            <td style={td}>{statusPill(r.status)}</td>
                            <td style={td}>{r.status === 'APPLIED' ? recordsCell(r.applied_count, r.applied_pct) : r.status === 'NO_OP' ? 'none needed' : '—'}</td>
                            <td style={td}>{has && <button style={linkBtn} onClick={() => setOpenChanges(o => ({ ...o, [key]: !o[key] }))}>{openChanges[key] ? 'Hide' : 'Examples'}</button>}</td>
                          </tr>
                          {has && openChanges[key] && (
                            <tr><td style={td} colSpan={5}>
                              <div style={{ fontSize: 12, color: T.textMuted }}>
                                {r.sample_changes.map((sc: any, j: number) => {
                                  const fc = sc.field_changes || {};
                                  const first = Object.values(fc)[0] as any;
                                  return first ? <div key={j} style={{ padding: '2px 0' }}>
                                    <span style={{ color: T.red, textDecoration: 'line-through' }}>{String(first.before)}</span>
                                    <span style={{ margin: '0 8px' }}>→</span>
                                    <span style={{ color: T.green }}>{String(first.after)}</span>
                                  </div> : null;
                                })}
                              </div>
                            </td></tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              ) : (
                <table style={tbl}>
                  <thead><tr>{['Field', 'What is wrong (business impact)', 'Sev', 'Status', 'Records affected', ...(k === 'assisted' ? ['Confidence'] : []), 'Recommended fix'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
                  <tbody>
                    {[...lanes[k]].sort((x: any, y: any) => (y.fail_count || 0) - (x.fail_count || 0)).slice(0, 25).map((r: any, i: number) => (
                      <tr key={r.id || i}>
                        <td style={td}><b>{Array.isArray(r.field) ? r.field.join(', ') : r.field}</b></td>
                        <td style={{ ...td, maxWidth: 300, color: T.textSecond }}>{r.business_impact}</td>
                        <td style={td}><Pill bg={(SEV[r.sev] || SEV.L).bg} fg={(SEV[r.sev] || SEV.L).fg}>{r.sev}</Pill></td>
                        <td style={td}>{statusPill(r.status)}</td>
                        <td style={td}>{recordsCell(r.fail_count, r.fail_pct)}</td>
                        {k === 'assisted' && <td style={td}>{r.conf || '—'}</td>}
                        <td style={{ ...td, maxWidth: 280, color: T.textMuted }}>{r.fix}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}
              {lanes[k].length > 25 && <div style={{ fontSize: 12, color: T.textMuted, marginTop: 6 }}>Showing top {k === 'auto' ? 40 : 25} of {lanes[k].length}.</div>}
            </div>
          )}
        </div>
      ))}

      {/* SME work list — actionable */}
      <div style={{ ...card }}>
        <div style={{ fontWeight: 800, color: T.brand, marginBottom: 4 }}>SME work list — what needs a person, most affected first</div>
        <div style={{ fontSize: 12, color: T.textMuted, marginBottom: 10 }}>Only checks with records currently failing. Ordered by severity, then by how many records are affected.</div>
        <div style={{ overflowX: 'auto' }}>
          <table style={tbl}>
            <thead><tr>{['#', 'Field', 'What is wrong', 'Sev', 'Records affected', 'Confidence', 'Recommended action', 'Owner'].map(h => <th key={h} style={th}>{h}</th>)}</tr></thead>
            <tbody>
              {sme_queue.map((r: any, i: number) => (
                <tr key={r.id || i}>
                  <td style={td}>{i + 1}</td>
                  <td style={td}><b>{Array.isArray(r.field) ? r.field.join(', ') : r.field}</b></td>
                  <td style={{ ...td, maxWidth: 300, color: T.textSecond }}>{r.business_impact}</td>
                  <td style={td}><Pill bg={(SEV[r.sev] || SEV.L).bg} fg={(SEV[r.sev] || SEV.L).fg}>{r.sev}</Pill></td>
                  <td style={td}>{recordsCell(r.fail_count, r.fail_pct)}</td>
                  <td style={td}>{r.conf || '—'}</td>
                  <td style={{ ...td, maxWidth: 280, color: T.textMuted }}>{r.fix}</td>
                  <td style={{ ...td, color: T.brandMid }}>—</td>
                </tr>
              ))}
              {sme_queue.length === 0 && <tr><td style={td} colSpan={8}>No checks currently failing require review.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

// status pill helper
const ST: Record<string, { bg: string; fg: string }> = {
  PASS: { bg: T.greenBg, fg: T.green }, FAIL: { bg: T.redBg, fg: T.red },
  APPLIED: { bg: T.brandLight, fg: T.brand }, NO_OP: { bg: T.bgSidebar, fg: T.textMuted },
  ERROR: { bg: T.amberBg, fg: T.amber },
};
const statusPill = (s?: string) => {
  if (!s) return <span style={{ color: T.textMuted }}>—</span>;
  const labelMap: Record<string, string> = { NO_OP: 'already clean', APPLIED: 'cleansed', PASS: 'pass', FAIL: 'fail', ERROR: 'error' };
  const c = ST[s.toUpperCase()] || ST.NO_OP;
  return <Pill bg={c.bg} fg={c.fg}>{labelMap[s.toUpperCase()] || s}</Pill>;
};

// ─────────────────────────────────────────────────────────────────────────────
// Phase 3 — Reconciliation (planned)
// ─────────────────────────────────────────────────────────────────────────────
const Phase3: React.FC = () => (
  <div style={{ ...card }}>
    <div style={{ fontWeight: 800, color: T.brand, fontSize: 15, marginBottom: 6 }}>Reconciliation — planned</div>
    <div style={{ fontSize: 13, color: T.textSecond, marginBottom: 14 }}>
      Run after data is loaded into the target (S/4HANA). Compares source vs target and confirms the migration landed correctly.
    </div>
    {[
      ['Technical reconciliation', 'Row counts, control totals on key numerics, distinct-value counts, foreign-key integrity, and null-rate deltas — source vs target, per object.'],
      ['Business reconciliation', 'KPI / measure parity (e.g. # active functional locations, equipment per location), domain-value coverage, and distribution drift.'],
      ['LLM variance explanation', 'Each source-vs-target difference is classified as expected (dropped empty column, deduplicated keys) or anomalous, and explained in business language — with a drafted sign-off narrative. The deterministic control totals remain the source of truth.'],
    ].map(([h, d]) => (
      <div key={h} style={{ padding: '10px 0', borderTop: `1px solid ${T.border}` }}>
        <div style={{ fontWeight: 700, color: T.textSecond, fontSize: 13 }}>{h}</div>
        <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>{d}</div>
      </div>
    ))}
  </div>
);

// ─── shared inline styles ─────────────────────────────────────────────────────
const card: React.CSSProperties = { background: T.bgCard, border: `1px solid ${T.border}`, borderRadius: 12, padding: 16 };
const sub:  React.CSSProperties = { fontSize: 12, fontWeight: 700, color: T.textMuted, marginBottom: 10 };
const tbl:  React.CSSProperties = { width: '100%', borderCollapse: 'collapse', fontSize: 12.5 };
const th:   React.CSSProperties = { textAlign: 'left', padding: '6px 10px', borderBottom: `2px solid ${T.border}`, color: T.textMuted, fontWeight: 700, whiteSpace: 'nowrap' };
const td:   React.CSSProperties = { padding: '6px 10px', borderBottom: `1px solid ${T.bgSidebar}`, color: T.textSecond, verticalAlign: 'top' };
const linkBtn: React.CSSProperties = { background: 'none', border: `1px solid ${T.border}`, borderRadius: 8, padding: '4px 10px', fontSize: 12, fontWeight: 600, color: T.brand, cursor: 'pointer', whiteSpace: 'nowrap' };

// ─── methodology / trust panel ────────────────────────────────────────────────
const MethodologyPanel: React.FC<{ m: any }> = ({ m }) => {
  const [open, setOpen] = useState(false);
  if (!m) return null;
  return (
    <div style={{ ...card, marginBottom: 16, padding: 0 }}>
      <button onClick={() => setOpen(o => !o)} style={{ width: '100%', textAlign: 'left', background: 'none', border: 'none', cursor: 'pointer', padding: '12px 16px', fontWeight: 700, color: T.brand, fontSize: 13 }}>
        {open ? '▾' : '▸'} How this report is calculated — methodology, thresholds & definitions
      </button>
      {open && (
        <div style={{ padding: '0 16px 16px', fontSize: 12.5, color: T.textSecond }}>
          <p style={{ marginTop: 0 }}><b>Readiness score.</b> {m.readiness_formula}</p>
          <p><b>SME work list order.</b> {m.sme_ordering}</p>
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
            <div>
              <div style={sub}>Thresholds</div>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                <li>Well populated: ≤ {m.thresholds.well_populated_max_missing_pct}% missing</li>
                <li>Sparse: ≥ {m.thresholds.sparse_min_missing_pct}% missing</li>
                <li>Barely populated: ≥ {m.thresholds.very_sparse_min_missing_pct}% missing</li>
              </ul>
              <div style={{ ...sub, marginTop: 10 }}>Score weights (points off 100)</div>
              <ul style={{ margin: 0, paddingLeft: 16 }}>
                {Object.entries(m.weights).map(([k, v]: any) => <li key={k}>{k.replace('W_', '').replace(/_/g, ' ').toLowerCase()}: {v}</li>)}
              </ul>
            </div>
            <div style={{ flex: 1, minWidth: 280 }}>
              <div style={sub}>Field disposition meaning</div>
              {Object.entries(m.disposition_glossary).map(([k, v]: any) => (
                <div key={k} style={{ marginBottom: 4 }}><Pill bg={(DISP[k] || DISP.CARRY).bg} fg={(DISP[k] || DISP.CARRY).fg}>{(DISP[k] || { label: k }).label}</Pill> <span style={{ color: T.textMuted }}>{v as string}</span></div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

const ProvenanceBanner: React.FC<{ a: DQAssessment }> = ({ a }) => (
  <div style={{ background: a.run_mode === 'sample' ? T.amberBg : T.greenBg, border: `1px solid ${a.run_mode === 'sample' ? T.amberBorder : T.greenBorder}`, borderRadius: 10, padding: '8px 14px', fontSize: 12.5, color: a.run_mode === 'sample' ? T.amber : T.green, marginBottom: 16 }}>
    {a.run_mode === 'sample'
      ? <><b>Indicative — based on a profiling sample.</b> Figures reflect the rows captured during profiling; re-run on the full extract before sign-off. </>
      : <><b>Validated — based on the full data extract.</b> </>}
    Rule scope: <b>{a.status === 'approved' ? 'approved rules only' : 'all rules'}</b> · {a.overall.total_rules} rules · {a.tables.length} object(s).
  </div>
);

// ─────────────────────────────────────────────────────────────────────────────
// HTML export — self-contained, business-readable, no JS/assets
// ─────────────────────────────────────────────────────────────────────────────
function buildHtmlReport(a: DQAssessment, label: string): string {
  const esc = (s: any) => String(s ?? '').replace(/[&<>"]/g, c => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;' } as any)[c]);
  const rc = (s: number) => (s >= 80 ? '#166534' : s >= 60 ? '#92400e' : '#991b1b');
  const bars = (d: Record<string, number>) => Object.entries(d || {}).filter(([, v]) => v > 0).sort((x, y) => y[1] - x[1])
    .map(([k, v]) => `<tr><td>${esc(k)}</td><td><b>${v}</b></td></tr>`).join('');

  const tableSec = a.tables.map((t: any) => `
    <div class="obj">
      <h3>${esc(t.table)} <span class="score" style="background:${rc(t.readiness)}">${Math.round(t.readiness)}/100</span></h3>
      <p class="muted">${t.num_rows.toLocaleString()} records · ${t.num_columns} fields${t.sample_rows ? ` · profiled on ${t.sample_rows.toLocaleString()} rows` : ''}</p>
      ${t.deductions?.length ? `<p class="muted"><b>Score breakdown:</b> ${t.deductions.map((d: any) => `<span style="color:#991b1b">${d.points}</span> ${esc(d.reason)}`).join(' · ')}</p>` : ''}
      <ul>${t.findings.map((f: any) => `<li class="${f.severity}">${esc(f.text)}${f.evidence && f.evidence.length ? `<ul>${f.evidence.map((e: string) => `<li>${esc(e)}</li>`).join('')}</ul>` : ''}${f.basis ? `<div class="basis">Basis: ${esc(f.basis)}</div>` : ''}</li>`).join('')}</ul>
      <table class="grid"><thead><tr><th>Field</th><th>Disposition</th><th>Missing %</th><th>Missing rows</th><th>Required</th><th>Reason</th></tr></thead>
      <tbody>${t.dispositions.map((d: any) => `<tr><td>${esc(d.column)}</td><td>${esc(d.disposition)}</td><td>${d.missing_pct ?? ''}%</td><td>${d.missing_count != null ? Number(d.missing_count).toLocaleString() : ''}</td><td>${d.mandatory ? 'Yes' : ''}</td><td>${esc(d.reason)}</td></tr>`).join('')}</tbody></table>
    </div>`).join('');

  const m = a.methodology || {};
  const methodSec = m.readiness_formula ? `
    <h2>How this report is calculated</h2>
    <p><b>Readiness score.</b> ${esc(m.readiness_formula)}</p>
    <p><b>SME work list order.</b> ${esc(m.sme_ordering)}</p>
    <p><b>Thresholds.</b> Well populated ≤ ${m.thresholds.well_populated_max_missing_pct}% missing · Sparse ≥ ${m.thresholds.sparse_min_missing_pct}% · Barely populated ≥ ${m.thresholds.very_sparse_min_missing_pct}%.</p>
    <table class="grid"><thead><tr><th>Disposition</th><th>Meaning</th></tr></thead><tbody>${Object.entries(m.disposition_glossary || {}).map(([k, v]) => `<tr><td>${esc(k)}</td><td>${esc(v)}</td></tr>`).join('')}</tbody></table>
    <table class="grid"><thead><tr><th>Apply lane</th><th>How items are selected</th></tr></thead><tbody>${Object.entries(m.apply_lanes || {}).map(([k, v]) => `<tr><td>${esc(k)}</td><td>${esc(v)}</td></tr>`).join('')}</tbody></table>` : '';

  const autoLane = (rows: any[]) => `
    <h3>${esc(LANE.auto.label)} (${rows.length})</h3><p class="muted">${esc((a.apply.rationale || {}).auto || '')}</p>
    <table class="grid"><thead><tr><th>Field</th><th>What the cleanse does</th><th>Status</th><th>Records changed</th></tr></thead>
    <tbody>${rows.slice(0, 100).map((r: any) => `<tr><td>${esc(Array.isArray(r.field) ? r.field.join(', ') : r.field)}</td><td>${esc(r.business_impact || r.fix)}</td><td>${esc(r.status || '')}</td><td>${r.status === 'APPLIED' && r.applied_count != null ? Number(r.applied_count).toLocaleString() + (r.applied_pct != null ? ` (${r.applied_pct}%)` : '') : (r.status === 'NO_OP' ? 'none needed' : '')}</td></tr>`).join('')}</tbody></table>`;

  const reviewLane = (k: 'assisted' | 'manual', rows: any[]) => `
    <h3>${esc(LANE[k].label)} (${rows.length})</h3><p class="muted">${esc((a.apply.rationale || ({} as any))[k] || '')}</p>
    <table class="grid"><thead><tr><th>Field</th><th>What is wrong (business impact)</th><th>Sev</th><th>Records affected</th>${k === 'assisted' ? '<th>Confidence</th>' : ''}<th>Recommended fix</th></tr></thead>
    <tbody>${[...rows].sort((x, y) => (y.fail_count || 0) - (x.fail_count || 0)).slice(0, 100).map((r: any) => `<tr><td>${esc(Array.isArray(r.field) ? r.field.join(', ') : r.field)}</td><td>${esc(r.business_impact)}</td><td>${esc(r.sev)}</td><td>${r.fail_count != null ? Number(r.fail_count).toLocaleString() + (r.fail_pct != null ? ` (${r.fail_pct}%)` : '') : ''}</td>${k === 'assisted' ? `<td>${esc(r.conf || '')}</td>` : ''}<td>${esc(r.fix)}</td></tr>`).join('')}</tbody></table>`;

  const ex = a.rollup?.execution;
  const healthSec = ex && (ex.rules_pass + ex.rules_fail) > 0 ? `
    <h3>Current data quality (measured by running the rules)</h3>
    <div class="kpis">
      <div class="kpi"><b style="color:${ex.pass_rate >= 80 ? '#166534' : ex.pass_rate >= 60 ? '#92400e' : '#991b1b'}">${ex.pass_rate ?? '—'}%</b>Checks passing</div>
      <div class="kpi"><b style="color:#991b1b">${ex.fields_failing.toLocaleString()}</b>Fields need attention</div>
      <div class="kpi"><b style="color:#166534">${ex.fields_cleansed.toLocaleString()}</b>Fields auto-cleansed</div>
      <div class="kpi"><b>${ex.rules_fail.toLocaleString()} / ${ex.rules_pass.toLocaleString()}</b>Checks fail / pass</div>
    </div>` : '';

  return `<!DOCTYPE html><html><head><meta charset="utf-8"><title>Migration Assessment — ${esc(a.entity_id || '')}</title>
  <style>
    body{font-family:-apple-system,Segoe UI,Roboto,sans-serif;color:#1e2d3d;max-width:980px;margin:24px auto;padding:0 20px;}
    h1{color:#1e3a6e;} h2{color:#1e3a6e;border-bottom:2px solid #c8d4e0;padding-bottom:6px;margin-top:34px;}
    h3{margin:18px 0 4px;} .muted{color:#64748b;font-size:13px;margin:2px 0;}
    .score{color:#fff;border-radius:8px;padding:2px 10px;font-size:14px;}
    .kpis{display:flex;gap:16px;flex-wrap:wrap;margin:14px 0;}
    .kpi{border:1px solid #c8d4e0;border-radius:10px;padding:12px 16px;min-width:120px;}
    .kpi b{font-size:24px;display:block;color:#1e3a6e;}
    table.grid{border-collapse:collapse;width:100%;font-size:12px;margin:8px 0 18px;}
    table.grid th,table.grid td{border:1px solid #e2e8f0;padding:5px 8px;text-align:left;vertical-align:top;}
    table.grid th{background:#f5f7fa;}
    ul{font-size:13px;} li.high{color:#991b1b;} li.warn{color:#92400e;}
    li ul{color:#64748b;font-size:12px;margin:2px 0;} .basis{color:#90aed0;font-style:italic;font-size:11px;}
    .obj{border:1px solid #c8d4e0;border-radius:10px;padding:14px 18px;margin:14px 0;}
    .banner{border-radius:8px;padding:8px 14px;font-size:12.5px;margin:10px 0;}
    .foot{color:#94a3b8;font-size:11px;margin-top:40px;border-top:1px solid #e2e8f0;padding-top:10px;}
  </style></head><body>
  <h1>Migration Readiness Assessment</h1>
  <p class="muted">Entity: <b>${esc(a.entity_id || '')}</b> · App: ${esc(a.app_nm || '')} · Run mode: ${esc(a.run_mode)} · Rule scope: ${esc(a.status)} · Generated: ${esc(label)}</p>
  <div class="banner" style="background:${a.run_mode === 'sample' ? '#fffbeb' : '#f0fdf4'};border:1px solid ${a.run_mode === 'sample' ? '#fcd34d' : '#86efac'};color:${a.run_mode === 'sample' ? '#92400e' : '#166534'}">
    ${a.run_mode === 'sample'
      ? '<b>Indicative — based on a profiling sample.</b> Figures reflect the rows captured during profiling; re-run on the full extract before sign-off.'
      : '<b>Validated — based on the full data extract.</b>'}
  </div>
  <div class="kpis">
    <div class="kpi"><b style="color:${rc(a.overall.readiness)}">${Math.round(a.overall.readiness)}</b>Readiness / 100</div>
    <div class="kpi"><b>${a.overall.objects}</b>Objects</div>
    <div class="kpi"><b>${a.overall.total_records.toLocaleString()}</b>Records</div>
    <div class="kpi"><b>${a.overall.total_rules}</b>DQ rules</div>
    <div class="kpi"><b style="color:#991b1b">${a.overall.blocker_count}</b>Blocker findings</div>
  </div>

  <h2>Phase 1 — Current Data Insight</h2>
  ${healthSec}
  <h3>Rules by dimension</h3><table class="grid"><tbody>${bars(a.rollup.by_dimension)}</tbody></table>
  ${tableSec}

  <h2>Phase 2 — Remediation Plan</h2>
  <p class="muted">Automated ${a.apply.counts.auto} (${(a.apply.auto_summary || {}).applied || 0} applied, ${(a.apply.auto_summary || {}).no_change_needed || 0} already clean) · Assisted ${a.apply.counts.assisted} · Manual ${a.apply.counts.manual}</p>
  ${autoLane(a.apply.lanes.auto)}
  ${reviewLane('assisted', a.apply.lanes.assisted)}
  ${reviewLane('manual', a.apply.lanes.manual)}

  <h2>Phase 3 — Reconciliation</h2>
  <p class="muted">Planned: technical control totals + business KPI parity + LLM-assisted variance explanation, run after load into the target.</p>

  ${methodSec}

  <div class="foot">Generated by Adriva — deterministic assessment from data profiling and approved DQ rules. Every figure traces to a profiling statistic or a DQ rule.</div>
  </body></html>`;
}

// ─────────────────────────────────────────────────────────────────────────────
// Main component
// ─────────────────────────────────────────────────────────────────────────────
interface Props { appNm: string; entityId: string; entityLabel?: string; }

const PHASES = [
  { key: 1, label: '1 · Migration Prep', sub: 'Current data insight' },
  { key: 2, label: '2 · Remediation', sub: 'Apply / cleanse / review' },
  { key: 3, label: '3 · Reconciliation', sub: 'Post-load verification' },
];

const DQAssessmentReport: React.FC<Props> = ({ appNm, entityId, entityLabel }) => {
  const [a, setA] = useState<DQAssessment | null>(null);
  const [loading, setLoading] = useState(false);
  const [phase, setPhase] = useState(1);
  const [runMode, setRunMode] = useState<'sample' | 'actual'>('sample');
  const [status, setStatus] = useState<'all' | 'approved'>('all');

  const load = useCallback(() => {
    if (!appNm || !entityId) return;
    setLoading(true);
    getAssessment(appNm, entityId, runMode, status)
      .then(res => setA(res))
      .catch(() => setA(null))
      .finally(() => setLoading(false));
  }, [appNm, entityId, runMode, status]);

  useEffect(() => { load(); }, [load]);

  const download = () => {
    if (!a) return;
    const html = buildHtmlReport(a, new Date().toLocaleString());
    const blob = new Blob([html], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `migration-assessment-${entityId}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const toggleStyle = (active: boolean): React.CSSProperties => ({
    padding: '4px 12px', fontSize: 12, fontWeight: 600, borderRadius: 8, cursor: 'pointer',
    border: `1px solid ${active ? T.brand : T.border}`, background: active ? T.brand : T.bgCard, color: active ? '#fff' : T.textMuted,
  });

  return (
    <div style={{ background: T.bg, minHeight: '100%', padding: '18px 22px' }}>
      {/* header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16, flexWrap: 'wrap', marginBottom: 16 }}>
        <div>
          <h3 style={{ margin: 0, color: T.brand, fontWeight: 800, fontSize: 20 }}>Migration Assessment</h3>
          <div style={{ fontSize: 13, color: T.textMuted, marginTop: 2 }}>{entityLabel || entityId}</div>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: T.textMuted }}>Data:</span>
            <span style={toggleStyle(runMode === 'sample')} onClick={() => setRunMode('sample')}>Sample</span>
            <span style={toggleStyle(runMode === 'actual')} onClick={() => setRunMode('actual')}>Full data</span>
          </div>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            <span style={{ fontSize: 11, color: T.textMuted }}>Rules:</span>
            <span style={toggleStyle(status === 'all')} onClick={() => setStatus('all')}>All</span>
            <span style={toggleStyle(status === 'approved')} onClick={() => setStatus('approved')}>Approved</span>
          </div>
          <button onClick={download} disabled={!a} style={{ ...toggleStyle(true), opacity: a ? 1 : 0.5 }}>⬇ Download report</button>
        </div>
      </div>

      {/* phase tabs */}
      <div style={{ display: 'flex', gap: 8, marginBottom: 18 }}>
        {PHASES.map(p => (
          <button key={p.key} onClick={() => setPhase(p.key)} style={{
            flex: 1, textAlign: 'left', padding: '10px 14px', borderRadius: 10, cursor: 'pointer',
            border: `1px solid ${phase === p.key ? T.brand : T.border}`,
            background: phase === p.key ? T.brand : T.bgCard, color: phase === p.key ? '#fff' : T.textSecond,
          }}>
            <div style={{ fontWeight: 800, fontSize: 13 }}>{p.label}</div>
            <div style={{ fontSize: 11, opacity: 0.85 }}>{p.sub}</div>
          </button>
        ))}
      </div>

      {/* body */}
      {loading && <div style={{ ...card, textAlign: 'center', color: T.textMuted }}>Building assessment…</div>}
      {!loading && !a && <div style={{ ...card, textAlign: 'center', color: T.textMuted }}>No assessment data available for this entity yet.</div>}
      {!loading && a && (
        <>
          <ProvenanceBanner a={a} />
          <MethodologyPanel m={a.methodology} />
          {phase === 1 && <Phase1 a={a} />}
          {phase === 2 && <Phase2 a={a} />}
          {phase === 3 && <Phase3 />}
        </>
      )}
    </div>
  );
};

export default DQAssessmentReport;